import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import API from "src/API2";
import "./createroomitem.css";

/* ── Inline icons ───────────────────────────────────────────────── */
const Svg = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcArrow = () => <Svg><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></Svg>;
const IcHash  = () => <Svg><line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" /></Svg>;
const IcBed   = () => <Svg><path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" /><path d="M2 17h20" /><path d="M6 10V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4" /></Svg>;
const IcLayer = () => <Svg><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></Svg>;
const IcCheck = () => <Svg><polyline points="20 6 9 17 4 12" /></Svg>;
const IcInfo  = () => <Svg size={14}><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></Svg>;

/* ── Helpers ────────────────────────────────────────────────────── */
const parseRoomNumbers = (input) => {
  if (!input) return [];
  if (input.includes("-")) {
    const [start, end] = input.split("-").map((s) => Number(s.trim()));
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return [];
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }
  return input
    .split(",")
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n));
};

const AddRoomItem = () => {
  const [hotelRoomId, setHotelRoomId] = useState("");
  const [floorId, setFloorId] = useState("");
  const [numberInput, setNumberInput] = useState("");
  const [error, setError] = useState("");
  const [rooms, setRooms] = useState([]);
  const [floors, setFloors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    toast.promise(
      Promise.all([API.get("rooms"), API.get("floors")]).then(([roomTypesResp, floorsResp]) => {
        setRooms(roomTypesResp.data.rooms || []);
        const sortedFloors = (floorsResp.data.floors || [])
          .slice()
          .sort((a, b) => Number(a.level) - Number(b.level));
        setFloors(sortedFloors);
      }),
      {
        success: "Room types and floors loaded",
        loading: "Loading room types and floors…",
        error: "Could not load data",
      },
    );
  }, []);

  const parsed = useMemo(() => parseRoomNumbers(numberInput), [numberInput]);
  const selectedRoom = rooms.find((r) => r.id === hotelRoomId);
  const selectedFloor = floors.find((f) => f.id === floorId);

  const canSubmit =
    !submitting && parsed.length > 0 && hotelRoomId && floorId;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError("");
    const toastId = toast.loading(`Creating ${parsed.length} room${parsed.length > 1 ? "s" : ""}…`);
    try {
      for (const number of parsed) {
        const response = await API.post(
          "roomitems",
          { number, hotelRoom_id: hotelRoomId, floor_id: floorId },
          { headers: { "Content-Type": "application/json" } },
        );
        if (!response.data.success) {
          setError(response.data.message);
          toast.error(response.data.message, { id: toastId });
          setSubmitting(false);
          return;
        }
      }
      toast.success("Rooms created", { id: toastId });
      navigate("/roomItem");
    } catch (err) {
      console.error(err);
      setError("An error occurred while creating the rooms.");
      toast.error("An error occurred while creating the rooms.", { id: toastId });
      setSubmitting(false);
    }
  };

  return (
    <div className="cri-page">
      {/* Hero */}
      <header className="cri-hero">
        <button type="button" className="cri-back" onClick={() => navigate("/roomItem")}>
          <IcArrow /> Back to rooms
        </button>
        <div className="cri-hero__body">
          <span className="cri-hero__eyebrow">Property setup</span>
          <h1 className="cri-hero__title">Add rooms</h1>
          <p className="cri-hero__sub">
            Add one or many physical rooms at once and assign them to a floor and
            room type. Reservations will be allocated against these.
          </p>
        </div>
      </header>

      <div className="cri-grid">
        <form className="cri-form" onSubmit={handleSubmit}>
          {/* Numbers */}
          <section className="cri-card">
            <header className="cri-card__head">
              <span className="cri-card__icon"><IcHash /></span>
              <div>
                <h3>Room numbers</h3>
                <p>Use a single number, a comma-separated list, or a range.</p>
              </div>
            </header>
            <label className="cri-field">
              <span>Numbers</span>
              <input
                type="text"
                value={numberInput}
                onChange={(e) => setNumberInput(e.target.value)}
                placeholder="e.g. 3   or   1,5,14,7   or   1-7"
                required
              />
            </label>
            <div className="cri-hint">
              <IcInfo /> <span>Examples: <code>3</code>, <code>1,5,14,7</code>, <code>1-7</code></span>
            </div>

            {parsed.length > 0 && (
              <div className="cri-numbers">
                <span className="cri-numbers__label">{parsed.length} room{parsed.length > 1 ? "s" : ""} will be created</span>
                <div className="cri-numbers__chips">
                  {parsed.slice(0, 30).map((n) => (
                    <span key={n} className="cri-num-chip">{n}</span>
                  ))}
                  {parsed.length > 30 && <span className="cri-num-chip cri-num-chip--more">+{parsed.length - 30} more</span>}
                </div>
              </div>
            )}
          </section>

          {/* Type & floor */}
          <section className="cri-card">
            <header className="cri-card__head">
              <span className="cri-card__icon cri-card__icon--blue"><IcBed /></span>
              <div>
                <h3>Assignment</h3>
                <p>Pick the room type and the floor these rooms belong to.</p>
              </div>
            </header>
            <div className="cri-grid-2">
              <label className="cri-field">
                <span>Room type</span>
                <select
                  value={hotelRoomId}
                  onChange={(e) => setHotelRoomId(e.target.value)}
                  required
                >
                  <option value="" disabled>Select a room type</option>
                  {rooms.map((r) => (
                    <option key={r.id} value={r.id}>{r.code}</option>
                  ))}
                </select>
              </label>
              <label className="cri-field">
                <span>Floor</span>
                <select
                  value={floorId}
                  onChange={(e) => setFloorId(e.target.value)}
                  required
                >
                  <option value="" disabled>Select a floor</option>
                  {floors.map((f) => (
                    <option key={f.id} value={f.id}>Floor {f.level}</option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          {error && <div className="cri-error">{error}</div>}
        </form>

        {/* Summary */}
        <aside className="cri-summary">
          <div className="cri-summary__card">
            <span className="cri-summary__eyebrow">Summary</span>
            <h3>Ready to add</h3>

            <ul className="cri-summary__list">
              <li>
                <span><IcHash /> Rooms to create</span>
                <strong>{parsed.length || "—"}</strong>
              </li>
              <li>
                <span><IcBed /> Room type</span>
                <strong>{selectedRoom?.code || "—"}</strong>
              </li>
              <li>
                <span><IcLayer /> Floor</span>
                <strong>{selectedFloor ? `Floor ${selectedFloor.level}` : "—"}</strong>
              </li>
            </ul>

            {parsed.length > 0 && selectedRoom && selectedFloor && (
              <div className="cri-summary__hint">
                You're about to create <strong>{parsed.length}</strong> room{parsed.length > 1 ? "s" : ""} on{" "}
                <strong>floor {selectedFloor.level}</strong> as type{" "}
                <strong>{selectedRoom.code}</strong>.
              </div>
            )}

            <div className="cri-summary__actions">
              <button
                type="button"
                className="cri-btn cri-btn--ghost"
                onClick={() => navigate("/roomItem")}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cri-btn cri-btn--primary"
                disabled={!canSubmit}
                onClick={handleSubmit}
              >
                {submitting ? "Adding…" : (<><IcCheck /> Add room{parsed.length > 1 ? "s" : ""}</>)}
              </button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default AddRoomItem;
